import React, { useState, useEffect, useContext, useMemo } from "react";
import styled from 'styled-components'
import { FiClock, FiTrendingUp, FiLayout } from 'react-icons/fi'
import { format, set, parseISO } from 'date-fns'
import {
  capitalizeFirstLetter,
  numberFormat,
  formatBopCode,
} from "../../../../../common/helper";
import PropTypes from "prop-types";
import { getStatusList } from "../../../../../lookups";

import DownloadFile from "../../../../DownloadFileComponent/DownloadFile";

import AuthContext from "../../../../../auth/auth-context";
import TradeContext from "../../../../../store/tradeState/tradeContext";

import StatusInfoHeader from "./status-info-header/status-info-header";
import ExecutedTradeSummary from "./ExecutedTradeSummary";
import Button from 'react-bootstrap/Button';
import axios from "axios";

// Styled components for a modern look consistent with TradeViewIntegrationContainer
const Container = styled.div`
  max-width: 980px;
`

const Title = styled.h2`
  margin: 0 0 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 800;
  font-size: 1.25rem;
  line-height: 1.2;
  letter-spacing: -0.2px;
  color: #212529;
  position: relative;
  padding-bottom: 8px;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: 0;
    width: 56px;
    height: 3px;
    border-radius: 999px;
    background: linear-gradient(90deg, #0d6efd 0%, #6f42c1 100%);
  }

  svg {
    color: #0d6efd;
    flex-shrink: 0;
  }
`

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06);
  overflow: hidden;
  margin-bottom: 16px;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
`

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`

const PairBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
  font-size: 0.95rem;
  color: #212529;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  padding: 6px 10px;
  border-radius: 999px;
`

const Meta = styled.span`
  color: #6c757d;
  font-size: 0.9rem;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  white-space: nowrap;
`

const Strong = styled.strong`
  color: #212529;
`

const Body = styled.div`
  border-top: 1px solid #e9ecef;
  padding: 12px 16px 14px;
  background: #ffffff;
`

const SectionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  margin: 0 0 12px;
  color: #212529;
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 24px;
`

const Row = styled.div`
  display: contents;
`

const Label = styled.span`
  color: #6c757d;
  padding: 6px 0;
`

const Value = styled.span`
  color: #212529;
  font-weight: 600;
  padding: 6px 0;
`

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
`

const ModalContent = styled.div`
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  max-width: 480px;
  width: 90%;
  padding: 24px;
`

const ModalTitle = styled.h3`
  margin: 0 0 12px;
  font-size: 1.25rem;
  font-weight: 700;
  color: #212529;
`

const ModalText = styled.p`
  margin: 0 0 24px;
  color: #6c757d;
  line-height: 1.6;
`

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`

function TradeView(props) {
  const authCtx = useContext(AuthContext);
  const tradeContext = useContext(TradeContext);
  const { updateTrade } = tradeContext;
  const [statusData, setStatusData] = useState([]);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelTradeLoading, setCancelTradeLoading] = useState(false);
  const [showCompleteTradeModal, setShowCompleteTradeModal] = useState(false);
  const [completeTradeLoading, setCompleteTradeLoading] = useState(false);

  console.log('TradeView summary props:', props);

  // Use context trade if available, otherwise use props
  const displayTrade = tradeContext.trade?.id === props.inTrade?.id 
    ? tradeContext.trade 
    : props.inTrade;

  // Initialize context trade when component mounts or props.inTrade changes
  useEffect(() => {
    if (props.inTrade?.id && (!tradeContext.trade?.id || tradeContext.trade.id !== props.inTrade.id)) {
      tradeContext.setTrade(props.inTrade);
    }
  }, [props.inTrade?.id]);

  // Sync context trade when props.inTrade updates (e.g., from polling)
  useEffect(() => {
    if (props.inTrade?.id && tradeContext.trade?.id === props.inTrade.id) {
      // Check if props.inTrade has newer data than context
      if (JSON.stringify(props.inTrade) !== JSON.stringify(tradeContext.trade)) {
        tradeContext.setTrade(props.inTrade);
      }
    }
  }, [props.inTrade]);

  // console.log('TradeContext:', tradeContext);
  // console.log('updateTrade from context:', updateTrade);
  // console.log('All context keys:', Object.keys(tradeContext));
  // console.log('Context updateTrade:', tradeContext.updateTrade);
  // console.log('Context updateTrade toString:', tradeContext.updateTrade?.toString());

  useEffect(() => {
    setStatusData(getStatusList());
  });

  const handleCancelTradeConfirm = async () => {
    const headers = {
      "Acting-as": "dealer",
      Authorization: `JWT ${authCtx?.token}`,
    };
    try {
      setCancelTradeLoading(true);
      // Make API call to cancel trade
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/trades/${displayTrade?.id}/set_cancelled/`,
        {},
        { headers: headers },
      );
      
      setCancelTradeLoading(false);
      setShowCancelModal(false);
      
      // Update trade status in the context state
      if (response.data.status === 'success') {
        tradeContext.setTrade({ ...displayTrade, status: "CANCELLED" });
        tradeContext.updateTrade(displayTrade?.id, { status: "CANCELLED" });
        
      }
      
      // Close the trade view
      // props.close();
    } catch (err) {
      console.error("Error cancelling trade:", err);
      setCancelTradeLoading(false);
      // Optionally show error to user
    }
  };

  const handleCompleteTradeConfirm = async () => {
    const headers = {
      "Acting-as": "dealer",
      Authorization: `JWT ${authCtx?.token}`,
    };
    try {
      setCompleteTradeLoading(true);
      // Make API call to complete trade
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/trades/${displayTrade?.id}/set_completed/`,
        {},
        { headers: headers },
      );
      
      setCompleteTradeLoading(false);
      setShowCompleteTradeModal(false);
      
      // Update trade status in the context state
      if (response.data.status === 'success') {
        tradeContext.setTrade({ ...displayTrade, status: "COMPLETED" });
        tradeContext.updateTrade(displayTrade?.id, { status: "COMPLETED" });
        
      }
      
      // Close the trade view
      // props.close();
    } catch (err) {
      console.error("Error completing trade:", err);
      setCompleteTradeLoading(false);
      // Optionally show error to user
    }
  };

  const pair = useMemo(() => {
    const trade = displayTrade || {}
    const left = trade?.trade_currency?.alpha_3 || trade?.trade_currency?.symbol
    const right = trade?.fx_currency?.alpha_3 || trade?.fx_currency?.symbol
    if (left || right) return `${left || '—'}/${right || '—'}`
    return '—/—'
  }, [displayTrade])
  
  const bookedAt = displayTrade?.trade_date 
    ? format(parseISO(displayTrade.trade_date), 'yyyy-MM-dd HH:mm')
    : null

  const handleTradeScreen = () => {
    if(displayTrade.status === 'BOOKED') {
      props.setTradeScreen('manual_booking');
    } else {
      props.setTradeScreen('integration');
    }
  }

  return (
    <Container>
      <StatusInfoHeader statusdata={statusData} trade={displayTrade} />
      {/* Show trade info */}
      
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
        <Title><FiLayout size={20} /> Trade Details</Title>
      {displayTrade.status !== 'CANCELLED' && displayTrade.status !== 'COMPLETED' && <div style={{width: '250px'}}><Button variant="success" style={{ width: '100%' }} onClick={handleTradeScreen}>Trade</Button></div>}
      </div>
      <ExecutedTradeSummary trade={displayTrade} />
      {/* Summary Card */}
      <Card>
        <Header>
          <Left>
            <PairBadge>{pair}</PairBadge>
            <Meta title={bookedAt}><FiClock /> {bookedAt ? bookedAt : '—'}</Meta>
          </Left>
          <Right>
            <Meta>Amount</Meta>
            <Strong>
              {displayTrade?.trade_currency?.symbol}
              {numberFormat(displayTrade?.trade_amount)}
            </Strong>
            <Meta><FiTrendingUp />Created Rate</Meta>
            <Strong>{displayTrade?.trade_rate}</Strong>
          </Right>
        </Header>
        <Body>
          <SectionTitle>Summary</SectionTitle>
          <Grid>
            <Row>
              <Label>Max Exc. Rate</Label>
              <Value>{displayTrade?.max_exchange_rate}</Value>
            </Row>
            <Row>
              <Label>FX Amount</Label>
              <Value>
                {displayTrade?.fx_currency?.symbol}
                {numberFormat(displayTrade?.fx_amount)}
              </Value>
            </Row>
            <Row>
              <Label>BOP Code</Label>
              <Value>{formatBopCode(displayTrade?.bop_code)}</Value>
            </Row>
            <Row>
              <Label>Trade Type</Label>
              <Value>{displayTrade?.allowance_type === 'fia' ? 'AIT' : 'SDA'}</Value>
            </Row>
            <Row>
              <Label>Trade Date</Label>
              <Value>{format(parseISO(displayTrade?.trade_date), 'MM/dd/yyyy HH:mm')}</Value>
            </Row>
            <Row>
              <Label>Bank</Label>
              <Value>{displayTrade?.bank?.display_name}</Value>
            </Row>
            <Row>
              <Label>Spread Rate</Label>
              <Value>{displayTrade?.client_spread_rate?.toString?.() || '-'}</Value>
            </Row>
            <Row>
              <Label>Reference</Label>
              <Value>{displayTrade?.trade_reference_number}</Value>
            </Row>
          </Grid>
        </Body>
      </Card>
      {/* Beneficiary Information */}
      <Card>
        <Body>
          <SectionTitle>Beneficiary Information</SectionTitle>
          <Grid>
            <Row><Label>Name</Label><Value>{displayTrade?.beneficiary?.name || ''}</Value></Row>
            <Row><Label>Friendly Name</Label><Value>{displayTrade?.beneficiary?.friendly_name || ''}</Value></Row>
            <Row><Label>IBAN</Label><Value>{displayTrade?.beneficiary?.account_number || ''}</Value></Row>
            <Row><Label>Client Reference</Label><Value>{displayTrade?.beneficiary?.client_reference_beneficiary || ''}</Value></Row>
            <Row><Label>Recipient Reference</Label><Value>{displayTrade?.beneficiary?.recipient_reference_beneficiary || ''}</Value></Row>
            <Row><Label>Beneficiary Address</Label><Value>{displayTrade?.beneficiary?.beneficiary_address || ''}</Value></Row>
            <Row><Label>Bank Country</Label><Value>{displayTrade?.beneficiary?.bank_country || ''}</Value></Row>
            <Row><Label>Bank Address</Label><Value>{displayTrade?.beneficiary?.bank_address || ''}</Value></Row>
            <Row><Label>Bank Name</Label><Value>{displayTrade?.beneficiary?.bank_name || ''}</Value></Row>
            <Row><Label>Branch / Sort Code</Label><Value>{displayTrade?.beneficiary?.bank_branch_code || ''}</Value></Row>
            <Row><Label>Currency</Label><Value>{displayTrade?.beneficiary?.currency || ''}</Value></Row>
            <Row><Label>Swift / Bic code</Label><Value>{displayTrade?.beneficiary?.swift_or_bic || ''}</Value></Row>
            <Row><Label>Crypto?</Label><Value>{displayTrade?.beneficiary?.crypto ? 'Yes' : 'No'}</Value></Row>
          </Grid>
        </Body>
      </Card>

      {/* Account details */}
      {/* Account Information */}
      <Card>
        <Body>
          <SectionTitle>Account Information</SectionTitle>
          <Grid>
            <Row><Label>Account Number</Label><Value>{displayTrade?.account?.account_number || ''}</Value></Row>
            <Row><Label>Account Type</Label><Value>{displayTrade?.account?.account_type?.description || ''}</Value></Row>
            <Row><Label>Branch</Label><Value>{displayTrade?.account?.bank_branch?.name || ''}</Value></Row>
            <Row><Label>Branch Code</Label><Value>{displayTrade?.account?.bank_branch?.branch_code || ''}</Value></Row>
            <Row><Label>Currency</Label><Value>{displayTrade?.account?.currency?.alpha_3 || ''}</Value></Row>
          </Grid>
        </Body>
      </Card>

      {/* Attachments */}
      <Card>
        <Body>
          <SectionTitle>Attachments</SectionTitle>
          <div>
            {displayTrade.trade_documents && displayTrade.trade_documents.length > 0 ? (
              displayTrade.trade_documents.map((t, index) => (
                <DownloadFile
                  key={index}
                  file_url={t.document_url}
                  file_name={`File-${index + 1}`}
                  uploaded_at={format(new Date(t.uploaded_at), "dd/MM/yy HH:mm")}
                  token={authCtx.token}
                />
              ))
            ) : (
              <span className="fileList">No files</span>
            )}
          </div>
        </Body>
      </Card>
      {displayTrade.status !== 'CANCELLED' && displayTrade.status !== 'COMPLETED' && (
        <div style={{ width: '100%', display: 'flex', gap: '12px' }}>
          <Button style={{ flex: 1 }} variant="danger" onClick={() => setShowCancelModal(true)}>Cancel Trade</Button>
          <Button style={{ flex: 1 }} variant="success" onClick={() => setShowCompleteTradeModal(true)}>Complete Trade</Button>
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {showCancelModal && (
        <ModalOverlay onClick={() => setShowCancelModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Cancel Trade</ModalTitle>
            <ModalText>
              Are you sure you want to cancel this trade? This action cannot be undone.
            </ModalText>
            <ModalActions>
              <Button variant="outline-secondary" onClick={() => setShowCancelModal(false)}>
                Go Back
              </Button>
              <Button 
                variant="danger" 
                onClick={handleCancelTradeConfirm}
              >
                Yes, Cancel Trade
              </Button>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
      {showCompleteTradeModal && (
        <ModalOverlay onClick={() => setShowCompleteTradeModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Complete Trade</ModalTitle>
            <ModalText>
              Are you sure you want to complete this trade? This action cannot be undone.
            </ModalText>
            <ModalActions>
              <Button variant="outline-secondary" onClick={() => setShowCompleteTradeModal(false)}>
                Go Back
              </Button>
              <Button 
                variant="success" 
                onClick={handleCompleteTradeConfirm}
              >
                Yes, Complete Trade
              </Button>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
}

TradeView.propTypes = {
  inTrade: PropTypes.object,
  data: PropTypes.array,
  addTrade: PropTypes.func,
  updateTradeStatus: PropTypes.func,
  executeTrade: PropTypes.func,
  close: PropTypes.func,
  isShown: PropTypes.bool,
  setTradeScreen: PropTypes.func,
};

export default TradeView;
